import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, fetchExpectedIncome, type ExpectedIncomeResponse } from "../services/expensesService";
import { Chip, Kpi, Notice, Spinner } from "./ui";
import { BUDGETS_PATH, COLLECTION_PAYMENTS_PATH, CRM_DEALS_PATH, CRM_FORECAST_PATH, CRM_INVOICES_PATH, CRM_QUOTES_PATH, crmQuotePath } from "../lib/flowRoutes";
import { money, monthLabel } from "../lib/format";
import { ui } from "../theme";

// Flujo → Ingresos → Ingresos esperados (#1290): los contratos corriendo (el pago
// fijo de cada negocio ganado), lo facturado por cobrar y lo que pronostica el
// CRM, mes a mes junto a lo que ya entró. Todo sale del CRM en vivo.

/** Meses que se muestran en la tabla: el mes en curso y los siguientes. */
const MONTHS_SHOWN = 6;

export default function ExpectedIncomeCard({ refreshKey }: { refreshKey?: unknown }) {
  const { t, i18n } = useTranslation(["common", "expensesStatements"]);
  const tf = (k: string, o?: Record<string, unknown>) => t(`expensesStatements:flow.${k}`, o);
  const language = i18n.language;
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const [data, setData] = useState<ExpectedIncomeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchExpectedIncome()
      .then((r) => alive && (setData(r), setError(null)))
      .catch((err) => alive && setError(apiErrorMessage(err) || "load"));
    return () => {
      alive = false;
    };
  }, [refreshKey]);

  const fmt = (v: number | null | undefined, currency = "MXN") => money(v, currency, language);
  const months = (data?.months || []).slice(0, MONTHS_SHOWN);
  // Lo que un pago sin aplicar ya cubrió no se espera otra vez (igual que en el flujo por mes).
  const receivableTotal = (data?.receivables || []).reduce((s, r) => s + Math.max(0, (r.balanceBase || 0) - (r.covered || 0)), 0);
  const overdue = (data?.receivables || []).filter((r) => r.overdue);
  // Cotizaciones enviadas sin vencer (#1546): fuente aparte del pronóstico.
  const quotes = data?.quotes || [];
  const hasQuotes = quotes.length > 0 || (data?.totals.quotes || 0) > 0;

  return (
    <section aria-label={tf("income.title")} className={`${c.card} space-y-4`} data-testid="expected-income">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h3 className={`text-sm font-semibold ${c.text}`}>
            <i className="ri-calendar-check-line mr-1 text-teal-600" />
            {tf("income.title")}
          </h3>
          <p className={`text-xs ${c.muted}`}>{tf("income.subtitle")}</p>
        </div>
        <div className="flex flex-wrap gap-3 text-xs">
          <a href={CRM_FORECAST_PATH} className="text-teal-600 hover:underline">
            <i className="ri-line-chart-line mr-1" />
            {tf("links.forecast")}
          </a>
          <a href={BUDGETS_PATH} className="text-teal-600 hover:underline">
            <i className="ri-scales-3-line mr-1" />
            {tf("links.budgets")}
          </a>
          <a href={COLLECTION_PAYMENTS_PATH} className="text-teal-600 hover:underline">
            <i className="ri-calendar-todo-line mr-1" />
            {tf("links.payments")}
          </a>
        </div>
      </div>

      {error && <Notice tone="warn">{error === "load" ? tf("loadError") : error}</Notice>}
      {!data && !error && <Spinner />}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Kpi label={tf("kpi.fixed")} value={fmt(data.monthlyFixed)} hint={tf("income.contractsCount", { count: data.contracts.length })} />
            <Kpi label={tf("series.receivable")} value={fmt(receivableTotal)} hint={tf("income.receivableHint", { count: data.receivables.length, overdue: overdue.length })} tone={overdue.length ? "bad" : undefined} />
            <Kpi label={tf("series.pipeline")} value={fmt(data.totals.pipeline)} hint={tf("income.pipelineHint", { amount: fmt(data.totals.pipelineFull) })} />
            <Kpi label={tf("series.quotes")} value={fmt(data.totals.quotes || 0)} hint={tf("income.quotesHint", { count: quotes.length, amount: fmt(data.totals.quotesFull || 0) })} />
            <Kpi label={tf("income.next")} value={fmt(months.slice(0, 3).reduce((s, m) => s + m.expected, 0))} hint={tf("income.nextHint")} />
          </div>
          {data.withoutRate.length > 0 && <Notice tone="warn">{tf("withoutRate", { currencies: data.withoutRate.join(", ") })}</Notice>}

          <div>
            <h4 className={`mb-1 text-xs font-semibold uppercase tracking-wide ${c.muted}`}>{tf("income.contracts")}</h4>
            {data.contracts.length === 0 ? (
              <p className={`text-xs ${c.muted}`}>
                {tf("income.noContracts")}{" "}
                <a href={CRM_DEALS_PATH} className="text-teal-600 hover:underline">
                  {tf("links.deals")}
                </a>
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className={`text-left text-xs ${c.muted}`}>
                      <th className="py-1 pr-2">{tf("income.client")}</th>
                      <th className="py-1 pr-2">{tf("income.frequency")}</th>
                      <th className="py-1 pr-2 text-right">{tf("income.perPeriod")}</th>
                      <th className="py-1 pr-2 text-right">{tf("income.monthly")}</th>
                      <th className="py-1">{tf("income.nextCut")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.contracts.map((k) => (
                      <tr key={k.dealId} className={`border-t ${c.border} ${c.text}`} data-testid={`contract-${k.dealId}`}>
                        <td className="py-1.5 pr-2">
                          {k.client}
                          {k.title && k.title !== k.client && <span className={`ml-2 text-xs ${c.muted}`}>{k.title}</span>}
                        </td>
                        <td className="py-1.5 pr-2">
                          <Chip className="bg-teal-500/15 text-teal-600">{tf(`income.freq.${k.frequency}`)}</Chip>
                          <span className={`ml-2 text-xs ${c.muted}`}>{tf("income.days", { days: k.days.join(" · ") })}</span>
                        </td>
                        <td className="whitespace-nowrap py-1.5 pr-2 text-right tabular-nums">{fmt(k.amount, k.currency)}</td>
                        <td className="whitespace-nowrap py-1.5 pr-2 text-right font-semibold tabular-nums">{k.monthly == null ? "—" : fmt(k.monthly)}</td>
                        <td className={`whitespace-nowrap py-1.5 text-xs tabular-nums ${c.muted}`}>{k.nextCut ? k.nextCut.date : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div data-testid="expected-quotes">
            <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
              <h4 className={`text-xs font-semibold uppercase tracking-wide ${c.muted}`}>{tf("income.quotes")}</h4>
              <a href={CRM_QUOTES_PATH} className="text-xs text-teal-600 hover:underline">
                <i className="ri-file-text-line mr-1" />
                {tf("links.quotes")}
              </a>
            </div>
            {quotes.length === 0 ? (
              <p className={`text-xs ${c.muted}`}>{tf("income.noQuotes")}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className={`text-left text-xs ${c.muted}`}>
                      <th className="py-1 pr-2">{tf("income.quoteFolio")}</th>
                      <th className="py-1 pr-2">{tf("income.client")}</th>
                      <th className="py-1 pr-2">{tf("income.quoteStage")}</th>
                      <th className="py-1 pr-2 text-right">{tf("income.quoteTotal")}</th>
                      <th className="py-1 pr-2 text-right">{tf("income.quoteWeighted")}</th>
                      <th className="py-1">{tf("income.quoteMonth")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotes.map((q) => (
                      <tr key={q.quoteId} className={`border-t ${c.border} ${c.text}`} data-testid={`quote-${q.quoteId}`}>
                        <td className="whitespace-nowrap py-1.5 pr-2">
                          <a href={crmQuotePath(q.quoteId)} className="font-medium text-teal-600 hover:underline">
                            {q.folio || q.title || q.quoteId}
                          </a>
                        </td>
                        <td className="py-1.5 pr-2">{q.client}</td>
                        <td className={`whitespace-nowrap py-1.5 pr-2 text-xs ${c.muted}`}>
                          {q.stage || tf("income.noDeal")} · {q.probability} %
                        </td>
                        <td className="whitespace-nowrap py-1.5 pr-2 text-right tabular-nums">{fmt(q.total, q.currency)}</td>
                        <td className="whitespace-nowrap py-1.5 pr-2 text-right font-semibold tabular-nums">{q.weighted == null ? "—" : fmt(q.weighted)}</td>
                        <td className={`whitespace-nowrap py-1.5 text-xs ${c.muted}`}>{q.period ? monthLabel(q.period, language) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {quotes.length > 0 && (
              <p className={`mt-1 text-xs ${c.muted}`}>
                {tf("income.quotesNote")}
                {(data.quotesUndated?.count || 0) > 0 && ` ${tf("income.quotesUndated", { count: data.quotesUndated?.count, amount: fmt(data.quotesUndated?.weighted) })}`}
              </p>
            )}
          </div>

          <div>
            <h4 className={`mb-1 text-xs font-semibold uppercase tracking-wide ${c.muted}`}>{tf("income.byMonth")}</h4>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className={`text-left text-xs ${c.muted}`}>
                    <th className="py-1 pr-2">{tf("table.month")}</th>
                    <th className="py-1 pr-2 text-right">{tf("series.contracts")}</th>
                    <th className="py-1 pr-2 text-right">{tf("series.receivable")}</th>
                    <th className="py-1 pr-2 text-right">{tf("series.pipeline")}</th>
                    {hasQuotes && <th className="py-1 pr-2 text-right">{tf("series.quotes")}</th>}
                    <th className="py-1 pr-2 text-right">{tf("income.expected")}</th>
                    <th className="py-1 text-right">{tf("series.actual")}</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((m) => (
                    <tr key={m.period} className={`border-t ${c.border} ${c.text}`} data-testid={`expected-${m.period}`}>
                      <td className="whitespace-nowrap py-1.5 pr-2">{monthLabel(m.period, language)}</td>
                      <td className="py-1.5 pr-2 text-right tabular-nums">{m.contracts ? fmt(m.contracts) : "—"}</td>
                      <td className="py-1.5 pr-2 text-right tabular-nums">{m.receivable ? fmt(m.receivable) : "—"}</td>
                      <td className="py-1.5 pr-2 text-right tabular-nums">{m.pipeline ? fmt(m.pipeline) : "—"}</td>
                      {hasQuotes && <td className="py-1.5 pr-2 text-right tabular-nums">{m.quotes ? fmt(m.quotes) : "—"}</td>}
                      <td className="py-1.5 pr-2 text-right font-semibold tabular-nums">{fmt(m.expected)}</td>
                      <td className={`py-1.5 text-right tabular-nums ${c.muted}`}>{m.phase === "future" ? "—" : fmt(m.actual)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className={`mt-1 text-xs ${c.muted}`}>
              {tf("income.note")}
              {data.undated.count > 0 && ` ${tf("undated", { count: data.undated.count, amount: fmt(data.undated.weighted) })}`}
              {data.receivables.length > 0 && (
                <>
                  {" "}
                  <a href={CRM_INVOICES_PATH} className="text-teal-600 hover:underline">
                    {tf("links.invoices")}
                  </a>
                </>
              )}
            </p>
          </div>
        </>
      )}
    </section>
  );
}
